import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';

import Page from 'components/layouts/page/Page';
import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import Search from 'components/shared/Search';
import ControlledTable from 'components/shared/table/ControlledTable';
import usePagination from 'hooks/usePagination';
import { DenialListItem } from 'models/DenialListItem';
import { PERMISSIONS } from 'models/Profile';
import { denialListItemQueryKeys, indexDenialListItems } from 'services/api/denialListItems';
import { serializeSort } from 'stores/helpers';
import { BodySmall, H2 } from 'styles/typography';
import PlusIcon from 'svg/PlusIcon';

import DeleteDenialListEntryModal from './DeleteDenialListEntryModal';
import denialListColumns from './denialListColumns';
import DenialListEmptyState from './DenialListEmptyState';
import DenialListEntryModal from './DenialListEntryModal';

// Matches the API default (Date Added desc, NULLS LAST) so the first load mirrors the mock.
const DEFAULT_SORT: SortingState = [{ id: 'dateAdded', desc: true }];

type ActiveModal = { type: 'add' } | { type: 'edit'; item: DenialListItem } | { type: 'delete'; item: DenialListItem };

function DenialListPage() {
  const pagination = usePagination();
  const [sort, setSort] = useState<SortingState>(DEFAULT_SORT);
  const [searchInput, setSearchInput] = useState(''); // immediate input value
  const [search, setSearch] = useState(''); // committed (debounced) search term
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);

  // Debounce the commit so search and its relevance sort are applied together — the
  // query never sees a new search with a stale sort. Mirrors AdmissionsPage/FilterBar.
  const applySearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setSort(value ? [{ id: 'search', desc: true }] : DEFAULT_SORT);
      }, 500),
    []
  );

  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    applySearch(value);
  };

  const queryParams = useMemo(
    () => ({
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
      sortBy: serializeSort(sort),
      search,
    }),
    [pagination.paging, sort, search]
  );

  // Reset to the first page whenever the committed search changes.
  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: denialListItemQueryKeys.index(queryParams),
    queryFn: ({ signal }) => indexDenialListItems(queryParams, signal),
  });

  const columns = useMemo(
    () =>
      denialListColumns({
        onEdit: (item) => setActiveModal({ type: 'edit', item }),
        onDelete: (item) => setActiveModal({ type: 'delete', item }),
      }),
    []
  );

  const closeModal = () => setActiveModal(null);

  const totalRecords = data?.meta.totalRecords ?? 0;
  const showEmptyState = !isLoading && totalRecords === 0 && !search;

  return (
    <Page>
      <Page.Header>
        <HeaderRow>
          <TitleGroup>
            <Title>Denial List</Title>
            <SearchContainer>
              <Search value={searchInput} onChange={handleSearchChange} placeholder='Search Patient' />
            </SearchContainer>
          </TitleGroup>
          <AccessGuard accessor={PERMISSIONS.denialListItemCreate}>
            <Button variant='primary' onClick={() => setActiveModal({ type: 'add' })}>
              <PlusIcon width={16} height={16} />
              Add New Entry
            </Button>
          </AccessGuard>
        </HeaderRow>
      </Page.Header>

      {!showEmptyState && (
        <Page.SubHeader>
          <BodySmall>
            {totalRecords} {totalRecords === 1 ? 'patient' : 'patients'} found
          </BodySmall>
        </Page.SubHeader>
      )}

      <Page.Content>
        {showEmptyState ? (
          <DenialListEmptyState onAdd={() => setActiveModal({ type: 'add' })} />
        ) : (
          <ControlledTable<DenialListItem>
            data={data?.data ?? []}
            columns={columns}
            loading={isLoading}
            pageCount={data?.meta.totalPages ?? 1}
            pagination={pagination}
            sorting={{ sortBy: sort, setSortBy: setSort }}
            // Sorting is applied by the API and it has no fallback ORDER BY, so an unsorted list
            // paginates non-deterministically — rows repeat across pages and others never appear.
            // Keep every header click on a direction rather than cycling back to no sort.
            enableSortingRemoval={false}
          />
        )}
      </Page.Content>

      {(activeModal?.type === 'add' || activeModal?.type === 'edit') && (
        <DenialListEntryModal
          denialListItem={activeModal.type === 'edit' ? activeModal.item : undefined}
          onClose={closeModal}
        />
      )}

      {activeModal?.type === 'delete' && (
        <DeleteDenialListEntryModal denialListItem={activeModal.item} onClose={closeModal} />
      )}
    </Page>
  );
}

export default DenialListPage;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SearchContainer = styled.div`
  width: 280px;
`;

const Title = styled(H2)`
  font-size: 20px;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
`;
